import Dexie, { PromiseExtended } from 'dexie';
import async from 'async';
import pathLib from 'path';

import { getHistoryRecordLimitTimestamp, HISTORY_RECORD_LIMIT, ERROR_RECORD_LIMIT } from '@/common/constant/sync';
import { SubTask } from '@/renderer/scripts/utils/TaskManager/types';

// eslint-disable-next-line no-use-before-define
const waitUntilReady = function waitUntilReady(target: Sync, name: PropertyKey, descriptor: PropertyDescriptor) {
  const fn = descriptor.value;
  descriptor.value = target.wrapReady(fn);
};

export default class Sync {
  /**
   * @description 当前登录态用户信息
   * @private
   * @type {SyncUserInfo}
   * @memberof Sync
   */
  private userInfo!: Sync.UserInfo;

  private db: Dexie = new Dexie('Sync');

  private syncInfoTable!: Dexie.Table<Sync.SyncInfo, Sync.SyncId>;

  private waitingTable!: Dexie.Table<Sync.WaitingOperateInfo, any>;

  private runningTable!: Dexie.Table<Sync.RunningOperateInfo, any>;

  private errorTable!: Dexie.Table<Sync.ErrorOperateInfo, any>;

  private historyTable!: Dexie.Table<Sync.HistoryOperateInfo, any>;

  public ready!: Promise<Sync>;

  constructor(options: { needClearTable: boolean }) {
    this.initDatabase(options.needClearTable);
  }

  private initDatabase(needClearTable: boolean) {
    // 建表 建索引
    const table = {
      SyncInfo: 'syncId, [userId+organizationId+libraryId], localPath, [remotePath+spaceId]',
      WaitingOperate: '++index, syncId, relativePath',
      RunningOperate: 'index, syncId, relativePath',
      ErrorOperate: 'relativePath, syncId',
      HistoryOperate: '++index, syncId, timestamp',
    };

    this.db.version(1).stores(table);

    this.syncInfoTable = this.db.table('SyncInfo');
    this.errorTable = this.db.table('ErrorOperate');
    this.waitingTable = this.db.table('WaitingOperate');
    this.runningTable = this.db.table('RunningOperate');
    this.historyTable = this.db.table('HistoryOperate');

    this.ready = new Promise(resolve => {
      if (this.db.isOpen()) {
        resolve(this);
        return;
      }
      this.db
        .open()
        .then(async () => {
          if (needClearTable) {
            // 每次重新打开数据库时需要清空超过 10 天的历史操作记录
            this.historyTable.where('timestamp').belowOrEqual(getHistoryRecordLimitTimestamp()).delete();
            // TODO: 每次重新打开数据库需要清空当前正在运行及等待运行队列
            await this.waitingTable.clear();
            await this.runningTable.clear();
          }
          resolve(this);
          logger.ui.info('腾讯云企业网盘 数据库打开成功...');
        })
        .catch(err => {
          logger.ui.error('腾讯云企业网盘 数据库打开失败', JSON.stringify(err));
        });
    });
  }

  public setUserInfo(userInfo: Sync.UserInfo) {
    this.userInfo = userInfo;
  }

  // eslint-disable-next-line class-methods-use-this
  public wrapReady(fn: Function) {
    return function wrapReady(...args: any[]): Promise<any> {
      // @ts-ignore
      return this.ready.then(() => fn.apply(this, args));
    };
  }

  checkIfExistLocalPath({ localPath, remotePath, spaceId }: Sync.PathInfo): Promise<boolean> {
    const formatPath = (path: string) => path.replaceAll(pathLib.sep, '/');
    return new Promise<any>((resolve, reject) => {
      // 检测是否存在相同的同步任务 && 存在包含关系的本地文件夹
      this.syncInfoTable
        .where(this.userInfo)
        .toArray()
        .then(res => {
          const existSameTask = res.some(
            item =>
              formatPath(item.localPath) === formatPath(localPath) &&
              item.remotePath === remotePath &&
              item.spaceId === spaceId,
          );
          if (existSameTask) {
            reject(new Error('SyncTaskExist'));
            return;
          }

          const existSameLocal = res.some(item => formatPath(item.localPath) === formatPath(localPath));
          if (existSameLocal) {
            reject(new Error('SameLocalFolderExist'));
            return;
          }

          const existChild = res.some(item => `${formatPath(item.localPath)}/`.includes(`${formatPath(localPath)}/`));
          if (existChild) {
            reject(new Error('SyncChildFolderExist'));
            return;
          }

          const existParent = res.some(item => `${formatPath(localPath)}/`.includes(`${formatPath(item.localPath)}/`));
          if (existParent) {
            reject(new Error('SyncParentFolderExist'));
            return;
          }

          if (res.length >= 5) {
            reject(new Error('SyncTaskLimit5'));
          }
          resolve(true);
        });
    });
  }

  /**
   * @description 添加同步任务
   * @param {Sync.SyncInfo} syncTask
   * @return {*}  {Promise<Sync.SyncId>}
   * @memberof Sync
   */
  @waitUntilReady
  addSyncInfo(syncTask: Sync.SyncInfo): Promise<Sync.SyncId> {
    return this.syncInfoTable.add(syncTask);
  }

  changeSyncTask(syncTask: Sync.SyncInfo): PromiseExtended<number> {
    return this.syncInfoTable.put(syncTask, syncTask.syncId);
  }

  /**
   * @description 获取同步任务列表
   * @return {*}  {PromiseExtended<any[]>}
   * @memberof Sync
   */
  @waitUntilReady
  getSyncTasks(): PromiseExtended<Sync.SyncInfo[]> {
    return this.syncInfoTable.where(this.userInfo).toArray();
  }

  /**
   * @description 获取同步任务列表(所有用户)
   * @return {*}  {PromiseExtended<any[]>}
   * @memberof Sync
   */
  @waitUntilReady
  getSyncTasksFromAllUser(): PromiseExtended<Sync.SyncInfo[]> {
    return this.syncInfoTable.toArray();
  }

  /**
   * @description  根据 id 获取同步任务信息
   * @return {*}  {PromiseExtended<any[]>}
   * @memberof Sync
   */
  @waitUntilReady
  getSyncTaskById(syncId: number): PromiseExtended<Sync.SyncInfo[]> {
    return this.syncInfoTable.where({ syncId }).toArray();
  }

  /**
   * @description 删除同步任务
   * @param {(number | number[])} syncId
   * @return {*}  {Promise<void>}
   * @memberof Sync
   */
  @waitUntilReady
  delSyncTask(syncId: number | number[]): Promise<void> {
    const syncIds = Array.isArray(syncId) ? syncId : [syncId];
    syncIds.forEach(syncId => {
      this.waitingTable.where({ syncId }).delete();
      this.runningTable.where({ syncId }).delete();
      this.errorTable.where({ syncId }).delete();
      this.historyTable.where({ syncId }).delete();
    });
    return this.syncInfoTable.bulkDelete(syncIds);
  }

  /**
   * @description 添加一条或多条等待同步的操作
   * @param {(Sync.WaitingOperateInfo | Sync.WaitingOperateInfo[])} operate
   * @return {*}  {Promise<number>}
   * @memberof Sync
   */
  @waitUntilReady
  async addToWaiting(operate: Sync.WaitingOperateInfo): Promise<number> {
    const { relativePath } = operate;
    return this.waitingTable.put(operate, relativePath);
  }

  /**
   * @description 获取 count 条等待同步操作列表
   * @param {number} [count=50]
   * @return {*}  {PromiseExtended<Sync.OperateRecord[]>}
   * @memberof Sync
   */
  @waitUntilReady
  async getWaitingList(count = 50): Promise<Sync.WaitingOperateInfo[]> {
    const list = await this.waitingTable.limit(count).toArray();
    return list;
  }

  /**
   * @description 等待执行的同步操作数量
   * @return {*}  {PromiseExtended<number>}
   * @memberof Sync
   */
  @waitUntilReady
  waitingListCount(syncId?: number): PromiseExtended<number> {
    if (syncId) {
      return this.waitingTable.where({ syncId }).count();
    }
    return this.waitingTable.count();
  }

  /**
   * @description 清空等待同步操作列表
   * @return {*}  {PromiseExtended<void>}
   * @memberof Sync
   */
  public clearWaitingList(syncId: Sync.SyncId): PromiseExtended<number>;

  public clearWaitingList(): PromiseExtended<void>;

  @waitUntilReady
  clearWaitingList(syncId?: Sync.SyncId): PromiseExtended<number | void> {
    if (syncId) {
      return this.waitingTable.where({ syncId }).delete();
    }
    return this.waitingTable.clear();
  }

  /**
   * @description 删除一条等待同步记录信息
   * @param {number} index
   * @return {*}  {PromiseExtended<number>}
   * @memberof Sync
   */
  @waitUntilReady
  delWaitingOperateInfo(params: { index?: number; relativePath?: string }): PromiseExtended<number> {
    const { index, relativePath } = params;
    if (index) {
      return this.waitingTable.where({ index }).delete();
    }
    return this.waitingTable.where({ relativePath }).delete();
  }

  /**
   * @description 添加一条或多条正在执行的同步操作，若存在相同记录则覆盖 (需要将等待列表中对应记录删除)
   * @param {(Sync.RunningOperateInfo | Sync.RunningOperateInfo[])} operate
   * @return {*}  {Promise<number>}
   * @memberof Sync
   */
  @waitUntilReady
  addToRunning(subTask: SubTask<Sync.RunningOperateInfo>): Promise<number> {
    const { status, speed, percent, info: operate, error } = subTask;
    this.waitingTable.delete(operate.index as number);
    const operateInfo: Sync.RunningOperateInfo = { ...operate, status, speed, percent, ...(error && { error }) };
    if (operateInfo.context) {
      delete operateInfo.context;
    }
    return this.runningTable.put(operateInfo);
  }

  async isRunning(index: number): Promise<boolean> {
    const res = await this.runningTable.where({ index }).toArray();
    return res.length > 0;
  }

  /**
   * @description 获取正在执行的同步操作列表
   * @return {*}  {Promise<Sync.OperateRecord[]>}
   * @memberof Sync
   */
  @waitUntilReady
  async getRunningList(syncId?: Sync.SyncId): Promise<Sync.RunningOperateInfo[]> {
    if (syncId) {
      return this.runningTable.where({ syncId }).toArray();
    }
    const list = await this.runningTable.toArray();
    return list;
  }

  /**
   * @description 获取正在执行的同步操作列表数量
   * @return {*}  {PromiseExtended<Sync.OperateRecord[]>}
   * @memberof Sync
   */
  @waitUntilReady
  runningListCount(syncId?: Sync.SyncId): PromiseExtended<number> {
    if (syncId) {
      return this.runningTable.where({ syncId }).count();
    }
    return this.runningTable.count();
  }

  /**
   * @description 删除一条正在同步的记录
   * @param {number} index
   * @return {*}  {PromiseExtended<number>}
   * @memberof Sync
   */
  @waitUntilReady
  delRunningRecord(index: number): PromiseExtended<number> {
    return this.runningTable.where({ index }).delete();
  }

  /**
   * @description 清空正在执行同步操作列表
   * @return {*}  {PromiseExtended<void>}
   * @memberof Sync
   */
  public clearRunningList(syncId: Sync.SyncId): PromiseExtended<number>;

  public clearRunningList(): PromiseExtended<void>;

  @waitUntilReady
  public clearRunningList(syncId?: Sync.SyncId): PromiseExtended<number | void> {
    if (syncId) {
      return this.runningTable.where({ syncId }).delete();
    }
    return this.runningTable.clear();
  }

  /**
   * @description 将失败操作添加至失败表
   * @param {SubTask<Sync.RunningOperateInfo>} subTask
   * @return {*}  {Promise<number>}
   * @memberof Sync
   */
  public async addToErrorRecord(subTask: SubTask<Sync.RunningOperateInfo>): Promise<number> {
    const { status, speed, percent, info: operate, error } = subTask;
    this.runningTable.delete(operate.index as number);
    const operateInfo: Sync.ErrorOperateInfo = { ...operate, status, speed, percent, ...(error && { error }) };
    if (operateInfo.context) {
      delete operateInfo.context;
    }
    const len = await this.errorTable.count();
    if (len > ERROR_RECORD_LIMIT) {
      // 错误日志只保留 ERROR_RECORD_LIMIT 条记录
      this.errorTable
        .reverse()
        .limit(len - ERROR_RECORD_LIMIT + 10)
        .delete();
    }
    return this.errorTable.put(operateInfo);
  }

  public async getErrorList(options: { currPage: number; pageSize?: number }): Promise<Sync.GetErrorListData> {
    const { currPage, pageSize = 10 } = options;
    const total = await this.errorTable.count();
    return this.errorTable
      .reverse()
      .offset((currPage - 1) * pageSize)
      .limit(pageSize)
      .toArray()
      .then(list => ({
        list,
        total,
      }));
  }

  clearErrorList(): PromiseExtended<number | void> {
    return this.errorTable.clear();
  }

  /**
   * @description 将已完成的同步操作添加至历史记录表
   * @param {(Sync.HistoryOperateInfo | Sync.HistoryOperateInfo[])} operate
   * @return {*}  {Promise<number>}
   * @memberof Sync
   */
  @waitUntilReady
  async addToHistory(operate: Sync.HistoryOperateInfo | Sync.HistoryOperateInfo[]): Promise<number> {
    const operates = Array.isArray(operate) ? operate : [operate];
    const idxs = operates.map(item => item.index);
    const syncIds = operates.map(item => item.syncId);
    this.runningTable.bulkDelete(idxs);

    // 每个 syncId 对应的历史记录只允许 1W 条
    await async.eachLimit(syncIds, 16, (syncId, next) => {
      const collection = this.historyTable.where({ syncId });
      collection.count().then(count => {
        if (count > HISTORY_RECORD_LIMIT) {
          collection
            .reverse()
            .limit(count - HISTORY_RECORD_LIMIT + 100)
            .delete()
            .then(() => next())
            // 删除失败不报错，继续后续的逻辑
            .catch(() => next());
        } else {
          next();
        }
      });
    });

    return this.historyTable.bulkPut(
      operates.map(operate => {
        if (operate.context) {
          delete operate.context;
        }
        if (operate.ins) {
          delete operate.ins;
        }
        return operate;
      }),
    );
  }

  /**
   * @description 获取同步操作历史
   * @param {SyncId} syncId 根据同步任务 id 获取历史列表
   * @param {{ nextIndex:number, limit:number }} options nextIndex:  下一个索引，limit:分页，默认 10
   * @return {*}  {Promise<Sync.GetHistoryListData>}
   * @memberof Sync
   */
  @waitUntilReady
  async getHistoryList(
    syncId: Sync.SyncId,
    options: { currPage: number; pageSize?: number },
  ): Promise<Sync.GetHistoryListData> {
    const { currPage, pageSize = 10 } = options;
    const total = await this.historyTable.where({ syncId }).count();
    return this.historyTable
      .where({ syncId })
      .reverse()
      .offset((currPage - 1) * pageSize)
      .limit(pageSize)
      .toArray()
      .then(list => ({
        list,
        total,
      }));
  }

  /**
   * @description 清空 syncId 同步相关的历史记录
   * @param {Sync.SyncId} syncId
   * @memberof Sync
   */
  @waitUntilReady
  clearHistoryList(syncId: Sync.SyncId): Promise<number> {
    return this.historyTable.where({ syncId }).delete();
  }

  /**
   * @description 删除一条历史记录
   * @param {number} index
   * @memberof Sync
   */
  @waitUntilReady
  delHistoryRecord(index: number): void {
    this.historyTable.where({ index }).delete();
  }
}
